import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import {
  changeEmployeeRole,
  deactivateEmployee,
  getEmployee,
  inviteEmployee,
  listEmployees,
  reactivateEmployee,
  resendInvitation,
  revokeInvitation,
  suspendEmployee,
  transferOwnership,
  type Employee,
  type EmployeePage,
  type EmployeeQuery,
  type EmployeeRole,
  type InviteEmployeeRequest,
} from './employeesApi';

export const EMPLOYEES_QUERY_KEY = ['employees'] as const;
export const employeesListKey = (query: EmployeeQuery) => [...EMPLOYEES_QUERY_KEY, 'list', query] as const;
export const employeeKey = (id: string) => [...EMPLOYEES_QUERY_KEY, 'detail', id] as const;

export function useEmployees(query: EmployeeQuery = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: employeesListKey(query),
    queryFn: () => listEmployees(query),
    enabled: options?.enabled ?? true,
    // Keeps the previous page on screen while the next one loads, so paging and typing in the search box
    // do not blank the table on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function useEmployee(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: employeeKey(id ?? ''),
    queryFn: () => getEmployee(id as string),
    enabled: Boolean(id) && (options?.enabled ?? true),
  });
}

/** Any employee mutation can change roles, membership and access, so the whole slice is invalidated. */
function useEmployeeMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, NormalizedApiError, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
      // Access resolution depends on who exists and what role they hold.
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

export const useInviteEmployee = () =>
  useEmployeeMutation<InviteEmployeeRequest, Employee>(inviteEmployee);

export const useChangeEmployeeRole = () =>
  useEmployeeMutation<{ id: string; role: EmployeeRole }, Employee>(({ id, role }) =>
    changeEmployeeRole(id, role),
  );

export const useSuspendEmployee = () => useEmployeeMutation<string, Employee>(suspendEmployee);
export const useReactivateEmployee = () => useEmployeeMutation<string, Employee>(reactivateEmployee);
export const useResendInvitation = () => useEmployeeMutation<string, Employee>(resendInvitation);
export const useRevokeInvitation = () => useEmployeeMutation<string, void>(revokeInvitation);
export const useTransferOwnership = () => useEmployeeMutation<string, Employee>(transferOwnership);
export const useDeactivateEmployee = () => useEmployeeMutation<string, void>(deactivateEmployee);

export type { Employee, EmployeePage, EmployeeQuery };
